import styled from 'styled-components';
import { Container } from '../../styles/sharedComponents';
import * as animations from '../../styles/animations';

// Navbar - sempre visível no topo (independente de outros elementos)
export const Nav = styled.nav`
  position: fixed;
  inset: 0 auto auto 50%;
  z-index: 9999;
  background: rgba(10, 10, 10, 0.0);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.0);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  height: 80px;
  overflow: visible;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Garante que funcione mesmo com transform em elementos pais */
  transform: translateX(-50%) translateZ(0);
  backface-visibility: hidden;

  /* will-change apenas quando animações estão habilitadas */
  @media (prefers-reduced-motion: no-preference) {
    will-change: transform, width, top;
  }

  &.scrolled {
    top: 24px;
    width: min(1280px, 95%);
    height: 64px;
    border-radius: ${props => props.theme.borders.radius.full};
    background: rgba(10, 10, 10, 0.6);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.08);
    border-bottom: none;
  }

  @media (max-width: 768px) {
    height: 64px;

    &.scrolled {
      top: 12px;
      width: min(100%, 94%);
      border-radius: 50px;
      height: 56px;
      background: rgba(10, 10, 10, 0.8);
    }
  }
`;

export const NavContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  overflow: visible;
`;

export const NavLogo = styled.a`
  font-size: 20px;
  font-weight: 700;
  color: #fafafa;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);

  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    &:hover {
      transform: translateY(-2px) scale(1.02);
      filter: drop-shadow(0 4px 12px rgba(255, 68, 68, 0.3));
      will-change: transform;
    }
  }
`;

export const LogoIcon = styled.img.attrs({
  loading: 'eager',
  decoding: 'async',
  width: '36',
  height: '36'
})`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

export const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 1100px) {
    display: none;
  }
`;

export const NavLink = styled.a<{ $active?: boolean }>`
  color: ${props => props.$active ? '#fafafa' : '#a1a1aa'};
  text-decoration: none;
  font-weight: 500;
  font-size: 15px;
  transition: color 0.2s ease;

  &:hover {
    color: #fafafa;
  }
`;

export const NavAuth = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 1100px) {
    gap: 12px;
  }
`;

export const LoginButton = styled.button`
  background: transparent;
  color: #a1a1aa;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${props => props.theme.borders.radius.full};
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  touch-action: manipulation;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fafafa;
    border-color: rgba(255, 255, 255, 0.2);
  }

  .full-text {
    display: inline;
  }

  .short-text {
    display: none;
  }

  @media (max-width: 1280px) {
    padding: 10px 16px;
    font-size: 14px;
    gap: 6px;
    max-width: 120px;
    min-height: 40px;

    .full-text {
      display: none;
    }

    .short-text {
      display: inline;
    }
  }

  @media (max-width: 480px) {
    padding: 9px 14px;
    font-size: 13px;
    gap: 5px;
    max-width: 110px;
    min-height: 38px;
  }
`;

export const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #a1a1aa;
  padding: 8px;
  cursor: pointer;
  z-index: 1001;
  position: relative;
  border-radius: ${props => props.theme.borders.radius.full};

  @media (max-width: 1100px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
  width: 24px;
  height: 24px;
}
`;

export const MobileMenuContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  background: rgba(10, 10, 10, 0.98);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 24px;
  transform: translateY(${props => props.$isOpen ? '0' : '-100%'});
  opacity: ${props => props.$isOpen ? '1' : '0'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  max-height: calc(100vh - 56px);
  overflow-y: auto;

  @media (min-width: 1101px) {
    display: none;
  }
`;

export const MobileNavLink = styled.a`
  display: block;
  color: #a1a1aa;
  text-decoration: none;
  font-weight: 500;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: color 0.2s ease;

  &:hover {
    color: #fafafa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const UserMenuContainer = styled.div`
position: relative;
`;

export const UserMenuDropdown = styled.div<{ $isOpen: boolean; $isClosing: boolean; $buttonWidth: number }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(15, 15, 15, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 8px;
  width: ${props => props.$buttonWidth}px;
  min-width: 180px;
  z-index: 1001;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: ${props => props.$isClosing ? animations.dropdownSlideOut : animations.dropdownSlideIn} 0.15s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  transform-origin: top right;
`;

export const UserInfo = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 4px;
  font-size: 12px;
  color: #71717a;
  word-break: break-word;
`;

export const LogoutButton = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: #fafafa;
  padding: 8px 12px;
  border-radius: 50px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    color: #ff4444;
  }
`;
